/**
 * Library icon artwork as data (KPR-1187, KPE-037 "icons on demand").
 *
 * WHY. Every icon a card, a collection, a choice or an avatar may wear used to
 * ship in main.js: the surfaces looked an icon up as `Icons[name]` on an
 * `import * as` of components/icons.js, so webpack could drop none of the
 * ~2,200 drawings (~560 KB gzip on every page). Now the BUILD writes one small
 * file per icon (scripts/build-icon-files.mjs -> public/icons/<NAME>.<hash>.json)
 * and components/common/LibraryIcon.js fetches the one it is asked to draw.
 *
 * WHAT A FILE HOLDS. `{ "v": 1, "attr": { viewBox, ... }, "child": [ { tag, attr, child? } ] }`
 * — the root <svg>'s own attributes and its element tree, exactly the shape
 * react-icons' GenIcon draws from. LibraryIcon builds it with
 * React.createElement (never innerHTML), and only the tags and attributes
 * below are ever drawn: the generator refuses anything else, and the drawer
 * refuses a file that holds anything else (`checkArtwork`).
 *
 * WHERE THE ARTWORK COMES FROM. Each registry entry (an export of icons.js) is
 * read through the first source in ICON_SOURCES that accepts it. Today the one
 * source is react-icons: the export is a GenIcon component, and calling it
 * returns the element tree, which is turned back into data. Since KPR-1189 an
 * entry may also be an Iconify reference, a `prefix:name` string
 * (`'iconoven:cement-mixer'`): `iconifySource(loadSet)` reads it from the
 * pinned @iconify-json package the caller loads (src/config/iconifySets.js,
 * build and test time only), and the artwork lands in the same kind of file.
 *
 * PURE: no imports. The generator (Node) and the jest tests both pass their
 * inputs in, so the two produce the same bytes.
 */

/** The file format's version; LibraryIcon refuses any other. */
export const ARTWORK_VERSION = 1;

/** The SVG elements a library icon may contain (every one the registry's sets use). */
export const ARTWORK_TAGS = Object.freeze([
	'circle', 'clipPath', 'defs', 'ellipse', 'g', 'line', 'linearGradient', 'path',
	'polygon', 'polyline', 'radialGradient', 'rect', 'stop', 'title',
]);

/** The attributes a library icon may carry, as React spells them. No event handler, href or style. */
export const ARTWORK_ATTRS = Object.freeze([
	'aria-hidden', 'clipPath', 'clipRule', 'cx', 'cy', 'd', 'fill', 'fillOpacity', 'fillRule',
	'gradientTransform', 'gradientUnits', 'height', 'id', 'offset', 'opacity', 'points', 'r', 'role',
	'rx', 'ry', 'stopColor', 'stroke', 'strokeLinecap', 'strokeLinejoin', 'strokeMiterlimit',
	'strokeOpacity', 'strokeWidth', 'transform', 'viewBox', 'width', 'x', 'x1', 'x2', 'y', 'y1', 'y2',
]);

/**
 * Attributes a source carries that draw nothing and are left out of the file:
 * a style STRING (IconBase replaces the root's style, and React refuses one on
 * a child), editor leftovers (`t`, `version`, `baseProfile`, `enableBackground`)
 * and the namespace declarations a browser does not need on an inline <svg>.
 */
export const DROPPED_ATTRS = Object.freeze(['style', 't', 'version', 'baseProfile', 'enableBackground', 'xmlns', 'xmlnsXlink', 'xmlSpace']);

const TAGS = new Set(ARTWORK_TAGS);
const ATTRS = new Set(ARTWORK_ATTRS);
const DROPPED = new Set(DROPPED_ATTRS);
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

function cleanAttrs(attrs, where) {
	const out = {};
	for (const key of Object.keys(attrs || {})) {
		if (key === 'children' || DROPPED.has(key)) continue;
		if (!ATTRS.has(key)) throw new Error(`${where}: attribute "${key}" is not one a library icon may carry (src/config/iconArtwork.js ARTWORK_ATTRS)`);
		const value = attrs[key];
		if (value === undefined || value === null) continue;
		if (typeof value !== 'string' && typeof value !== 'number') throw new Error(`${where}: attribute "${key}" is not text`);
		out[key] = String(value);
	}
	return out;
}

function treeFromElements(children, where) {
	const list = (Array.isArray(children) ? children : [children]).flat(Infinity).filter(c => c !== undefined && c !== null && c !== false);
	return list.map((element, index) => {
		const tag = element && element.type;
		if (typeof tag !== 'string' || !TAGS.has(tag)) throw new Error(`${where}: <${typeof tag === 'string' ? tag : typeof tag}> is not an element a library icon may contain (ARTWORK_TAGS)`);
		const node = { tag, attr: cleanAttrs(element.props, `${where} > ${tag}[${index}]`) };
		const child = treeFromElements(element.props && element.props.children, `${where} > ${tag}[${index}]`);
		if (child.length) node.child = child;
		return node;
	});
}

/**
 * A react-icons component's artwork. A react-icons export is
 * `GenIcon(tree)`: called, it returns <IconBase attr={tree.attr}> with the
 * tree's elements as children (react-icons lib Tree2Element), which is read
 * back into `{ attr, child }`. Throws for anything that is not one.
 */
export function reactIconsArtwork(Component, where = 'icon') {
	const element = Component({});
	const props = element && element.props;
	if (!props || !props.attr || typeof props.attr !== 'object') throw new Error(`${where}: not a react-icons icon (no GenIcon tree)`);
	const artwork = { v: ARTWORK_VERSION, attr: cleanAttrs(props.attr, `${where} <svg>`), child: treeFromElements(props.children, where) };
	if (!artwork.attr.viewBox) throw new Error(`${where}: the icon has no viewBox`);
	return artwork;
}

/**
 * An Iconify reference: `prefix:name`, both lowercase kebab case, as Iconify
 * spells an icon (`iconoven:cement-mixer`, `tabler:truck`).
 */
export const ICONIFY_REF = /^([a-z0-9]+(?:-[a-z0-9]+)*):([a-z0-9]+(?:-[a-z0-9]+)*)$/;

/** True when a registry entry names an Iconify icon. */
export function isIconifyRef(entry) {
	return typeof entry === 'string' && ICONIFY_REF.test(entry);
}

/**
 * True when an export of icons.js is a library icon: a react-icons component
 * or an Iconify reference. (antd icons are objects and are not; KPR-999.)
 */
export function isRegistryIcon(entry) {
	return typeof entry === 'function' || isIconifyRef(entry);
}

const XML_ENTITIES = Object.freeze({ amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" });

function decodeEntities(text, where) {
	return text.replace(/&([a-zA-Z]+|#[0-9]+|#x[0-9a-fA-F]+);/g, (whole, ref) => {
		if (own(XML_ENTITIES, ref)) return XML_ENTITIES[ref];
		if (ref[0] === '#') return String.fromCodePoint(ref[1] === 'x' ? parseInt(ref.slice(2), 16) : parseInt(ref.slice(1), 10));
		throw new Error(`${where}: the entity ${whole} is not one an icon may use`);
	});
}

/** An SVG attribute's name as React spells it: `stroke-width` -> `strokeWidth`; `aria-*` stays. */
export function reactAttrName(name) {
	if (name.startsWith('aria-')) return name;
	return name.replace(/[-:]([a-z])/g, (_, letter) => letter.toUpperCase());
}

const ATTR_RE = /\s*([A-Za-z_:][-A-Za-z0-9_:.]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/y;

/**
 * Reads an Iconify `body` (the inside of an <svg>, as markup) into the
 * artwork's element tree. A small XML reader, not an HTML one: elements,
 * quoted attributes and self-closing tags. Text, comments, CDATA,
 * processing instructions and unknown entities are refused rather than
 * guessed at, and every tag and attribute goes through the same whitelist
 * as a react-icons drawing.
 */
export function parseSvgBody(body, where = 'icon') {
	if (typeof body !== 'string') throw new Error(`${where}: the icon has no body`);
	const root = { child: [] };
	const stack = [root];
	let at = 0;
	while (at < body.length) {
		const lt = body.indexOf('<', at);
		const text = body.slice(at, lt === -1 ? body.length : lt);
		if (text.trim()) throw new Error(`${where}: the icon holds text ("${text.trim().slice(0, 20)}"), which a library icon may not`);
		if (lt === -1) break;
		const next = body[lt + 1];
		if (next === '!' || next === '?') throw new Error(`${where}: the icon holds a comment, CDATA or a declaration, which a library icon may not`);
		if (next === '/') {
			const gt = body.indexOf('>', lt);
			const tag = gt === -1 ? '' : body.slice(lt + 2, gt).trim();
			const open = stack[stack.length - 1];
			if (stack.length === 1 || open.tag !== tag) throw new Error(`${where}: </${tag}> closes nothing open`);
			stack.pop();
			at = gt + 1;
			continue;
		}
		const nameMatch = /^[A-Za-z][-A-Za-z0-9:]*/.exec(body.slice(lt + 1));
		if (!nameMatch) throw new Error(`${where}: a tag has no name`);
		const tag = nameMatch[0];
		const path = `${where} > ${tag}`;
		if (!TAGS.has(tag)) throw new Error(`${path}: <${tag}> is not an element a library icon may contain (ARTWORK_TAGS)`);
		const raw = {};
		let cursor = lt + 1 + tag.length;
		for (;;) {
			ATTR_RE.lastIndex = cursor;
			const m = ATTR_RE.exec(body);
			if (!m) break;
			raw[reactAttrName(m[1])] = decodeEntities(m[2] !== undefined ? m[2] : m[3], path);
			cursor = ATTR_RE.lastIndex;
		}
		const close = /^\s*(\/?)>/.exec(body.slice(cursor));
		if (!close) throw new Error(`${path}: the tag is not well formed`);
		const node = { tag, attr: cleanAttrs(raw, path) };
		stack[stack.length - 1].child.push(node);
		at = cursor + close[0].length;
		if (!close[1]) {
			node.child = [];
			stack.push(node);
		}
	}
	if (stack.length > 1) throw new Error(`${where}: <${stack[stack.length - 1].tag}> is never closed`);
	const prune = nodes => nodes.map(node => {
		if (node.child && node.child.length) return { ...node, child: prune(node.child) };
		const { child, ...rest } = node;
		return rest;
	});
	return prune(root.child);
}

const ICONIFY_TRANSFORMS = Object.freeze(['rotate', 'hFlip', 'vFlip']);

/**
 * An Iconify icon's artwork, from its set as the @iconify-json package holds
 * it (`icons.json`: `{ prefix, icons, aliases?, width?, height?, left?, top? }`).
 * An alias is followed to its parent. A rotated or flipped icon is refused:
 * pick the drawing itself. The root keeps SVG's own defaults for what the
 * body leaves unset (stroke none, stroke width 1) and fills with the current
 * color, as Iconify draws it; LibraryIcon's IconBase defaults (stroke width 0)
 * would otherwise thin out a stroked body.
 */
export function iconifyArtwork(ref, name = ref, iconSet) {
	const m = ICONIFY_REF.exec(typeof ref === 'string' ? ref : '');
	if (!m) throw new Error(`${name}: "${ref}" is not an Iconify reference (prefix:name)`);
	const [, prefix, iconName] = m;
	if (!iconSet || typeof iconSet !== 'object' || !iconSet.icons) throw new Error(`${name}: the Iconify set "${prefix}" is not one keepr has pinned (src/config/iconifySets.js)`);
	if (iconSet.prefix !== prefix) throw new Error(`${name}: the set loaded for "${prefix}" is "${iconSet.prefix}"`);
	let key = iconName;
	const seen = new Set();
	let icon = null;
	let transformed = false;
	for (;;) {
		if (seen.has(key)) throw new Error(`${name}: ${prefix}:${iconName} is an alias loop`);
		seen.add(key);
		if (own(iconSet.icons, key)) {
			icon = iconSet.icons[key];
			break;
		}
		const alias = iconSet.aliases && own(iconSet.aliases, key) ? iconSet.aliases[key] : null;
		if (!alias) throw new Error(`${name}: the set "${prefix}" has no icon "${iconName}"`);
		if (ICONIFY_TRANSFORMS.some(t => alias[t])) transformed = true;
		key = alias.parent;
	}
	if (transformed || ICONIFY_TRANSFORMS.some(t => icon[t])) throw new Error(`${name}: ${prefix}:${iconName} is rotated or flipped; pick the drawing itself`);
	const pick = field => (icon[field] !== undefined ? icon[field] : iconSet[field]);
	const left = pick('left') || 0;
	const top = pick('top') || 0;
	const width = pick('width') || 16;
	const height = pick('height') || 16;
	const artwork = {
		v: ARTWORK_VERSION,
		attr: { viewBox: `${left} ${top} ${width} ${height}`, fill: 'currentColor', stroke: 'none', strokeWidth: '1' },
		child: parseSvgBody(icon.body, name),
	};
	if (!artwork.child.length) throw new Error(`${name}: ${prefix}:${iconName} draws nothing`);
	return artwork;
}

/**
 * The Iconify source. `loadSet(prefix)` returns the pinned set's icons.json
 * (or null for a prefix keepr has not pinned); it is passed in so this file
 * stays pure and the app never bundles a set.
 */
export function iconifySource(loadSet) {
	return Object.freeze({
		id: 'iconify',
		accepts: isIconifyRef,
		artwork: (ref, name) => iconifyArtwork(ref, name, loadSet(ICONIFY_REF.exec(ref)[1])),
	});
}

/**
 * Where each registry entry's artwork comes from: the first source whose
 * `accepts(entry)` is true draws it. These are the sources that need no
 * input; the Iconify one needs its pinned sets, so the generator and the jest
 * resolver use src/config/iconifySets.js REGISTRY_ICON_SOURCES, which is
 * these plus `iconifySource(loadIconifySet)`.
 */
export const ICON_SOURCES = Object.freeze([
	Object.freeze({ id: 'react-icons', accepts: entry => typeof entry === 'function', artwork: reactIconsArtwork }),
]);

/** One registry entry's artwork, through the first source that accepts it. */
export function artworkOf(name, entry, sources = ICON_SOURCES) {
	const source = sources.find(s => s.accepts(entry));
	if (!source) throw new Error(`${name}: no icon source accepts this registry entry (src/config/iconArtwork.js ICON_SOURCES)`);
	return source.artwork(entry, name);
}

function checkTree(nodes, depth) {
	if (!Array.isArray(nodes)) return 'child is not a list';
	if (depth > 16) return 'the tree is too deep';
	for (const node of nodes) {
		if (!node || typeof node !== 'object' || typeof node.tag !== 'string' || !TAGS.has(node.tag)) return `an element is not one a library icon may contain`;
		const bad = checkAttrs(node.attr);
		if (bad) return bad;
		if (own(node, 'child')) {
			const inner = checkTree(node.child, depth + 1);
			if (inner) return inner;
		}
	}
	return null;
}

function checkAttrs(attr) {
	if (!attr || typeof attr !== 'object' || Array.isArray(attr)) return 'attr is not an object';
	for (const key of Object.keys(attr)) {
		if (!ATTRS.has(key)) return `attribute "${key}" is not one a library icon may carry`;
		if (typeof attr[key] !== 'string') return `attribute "${key}" is not text`;
	}
	return null;
}

/**
 * Null when `artwork` is a file LibraryIcon may draw, else a sentence saying
 * what is wrong with it. The drawer's own guard: a file it did not expect (a
 * page the host served in its place, a hand edit) is refused, not drawn.
 */
export function checkArtwork(artwork) {
	if (!artwork || typeof artwork !== 'object' || Array.isArray(artwork)) return 'not an artwork object';
	if (artwork.v !== ARTWORK_VERSION) return `version ${artwork.v} is not ${ARTWORK_VERSION}`;
	const bad = checkAttrs(artwork.attr);
	if (bad) return bad;
	if (typeof artwork.attr.viewBox !== 'string') return 'no viewBox';
	return checkTree(artwork.child, 0);
}

/** An artwork file's bytes: compact JSON, keys in the order written, a trailing newline. */
export function renderArtworkFile(artwork) {
	return `${JSON.stringify(artwork)}\n`;
}

/** The file an icon's artwork is published as, under public/icons/. */
export function iconFileName(name, hash) {
	return `${name}.${hash}.json`;
}

/** How many hex characters of the content's SHA-256 name a file (as CRA names its chunks). */
export const ICON_HASH_LENGTH = 8;

/**
 * The committed name -> hash map (src/config/iconFiles.json): one icon per
 * line, sorted by name, tabs, a trailing newline, so a diff names the icon
 * whose artwork changed.
 */
export function renderIconFiles(hashes) {
	const names = Object.keys(hashes).sort();
	if (!names.length) return '{}\n';
	return `{\n${names.map(name => `\t${JSON.stringify(name)}: ${JSON.stringify(hashes[name])}`).join(',\n')}\n}\n`;
}
